/**
 * Turns a failed sign-in into something a user can act on.
 *
 * Wording lives here, not in the API layer: an endpoint reports a
 * machine-readable problem and never authors copy, because it does not know
 * who is asking (issue #1035). This is the caller that words it.
 *
 * Two properties are load-bearing and easy to destroy by "improving" the copy:
 *
 * - **Never repeat what the server said.** `POST /users/login` answers a bad
 *   login with a bare `Invalid credentials` string, and its catch-all answers
 *   with the raw driver `error.message` -- which can carry SQL. Nothing from
 *   the response body reaches the screen; only the status selects a sentence.
 * - **Never distinguish an unknown user from a wrong password.** The backend
 *   deliberately compares against a dummy hash so both cost the same time and
 *   both answer 401. Copy that says "no such user" would hand back the
 *   difference the backend spends effort hiding.
 */

import axios from 'axios';

/**
 * What is known about a failed sign-in, and all that is known: a transport
 * failure has no status, and a response that never arrived has no code either.
 * Deliberately not the axios error -- that object still holds the request
 * config, and with it the credentials (issue #1181).
 */
export interface LoginFailure {
  readonly status: number | undefined;
  readonly code: string | undefined;
}

/** axios reports a timeout under one of these, depending on `transitional`. */
const TIMEOUT_CODES = ['ECONNABORTED', 'ETIMEDOUT'];

/**
 * Not an axios code: our own, for a 2xx that carried no session token. It has
 * a status like any other answer, so it needs naming to avoid being worded as
 * "the server returned an error (HTTP 200)".
 */
export const NO_TOKEN_CODE = 'ERR_NO_TOKEN';

/**
 * Not an axios code either: the throw never came from the request at all, so
 * nothing about a connection is known. Distinguished because the absence of a
 * status otherwise reads as "no response ever arrived", which for this branch
 * points the user -- and support -- at the wrong thing entirely.
 */
export const UNEXPECTED_CODE = 'ERR_UNEXPECTED';

const CONTACT_ADMIN = 'contact your administrator if the problem persists.';

export const loginFailureMessage = ({ status, code }: LoginFailure): string => {
  if (code === NO_TOKEN_CODE) {
    return `Sign-in failed: the server accepted the credentials but returned no session token. Please ${CONTACT_ADMIN}`;
  }
  if (code === UNEXPECTED_CODE) {
    return `Sign-in failed unexpectedly. Try again, or ${CONTACT_ADMIN}`;
  }
  if (status === 401) {
    return 'Invalid username or password.';
  }
  if (status === 429) {
    // Rate-limited. The catch-all's "Try again" is the one instruction that
    // makes this worse, and a whole office behind one NAT egress shares the
    // bucket -- so the wait is the point, not the retry.
    return `Too many sign-in attempts. Wait a moment and try again, or ${CONTACT_ADMIN}`;
  }
  if (status === 403) {
    // Reached the API and was refused. Indistinguishable from a 500 under the
    // catch-all, but the remedy is not the same: retrying will not help.
    return `Your account is not permitted to sign in here. Please ${CONTACT_ADMIN}`;
  }
  if (status === undefined) {
    // No response at all. Which of the two it is matters to the user: a
    // timeout means the server is reachable but not answering, so retrying is
    // reasonable; ERR_NETWORK means the request never landed.
    if (code !== undefined && TIMEOUT_CODES.includes(code)) {
      return `The server did not respond in time. It may be unavailable -- try again, or ${CONTACT_ADMIN}`;
    }
    return `Cannot reach the server. Check your connection, or ${CONTACT_ADMIN}`;
  }
  return `Sign-in failed: the server returned an error (HTTP ${status}). Try again, or ${CONTACT_ADMIN}`;
};

/**
 * The single-sign-on equivalent. Split from the password path because the two
 * fail for different reasons and only one of them is ever the user's to fix:
 * an SSO failure is a configuration or provider problem, so the copy points at
 * the fallback that is actually available rather than at the credentials.
 */
export const ssoFailureMessage = (): string => `Single sign-on failed. Try again, sign in with a username and password, or ${CONTACT_ADMIN}`;

/**
 * Reads the only two things worth keeping off a caught error, and nothing
 * else. `catch` hands over `unknown`, and an axios error's `config` still
 * holds the request -- for a sign-in, that means the username and password --
 * so the whole object must never be forwarded or logged (issue #1181).
 *
 * A transport failure has no `response` and therefore no status; that absence
 * is meaningful, and `loginFailureMessage` reads it as "never got an answer".
 */
export const loginFailureFrom = (error: unknown): LoginFailure => {
  // Not an axios failure at all: a throw from a request interceptor, from the
  // tracing wrapper, or a TypeError from anywhere inside the call. Reporting
  // it with no code would have it worded as an unreachable server, sending
  // the user to check a connection that is fine and support to the network.
  // `isAxiosError` is a type guard and reads no more of the object than the
  // cast below does, so `config` is still never touched (issue #1181).
  if (!axios.isAxiosError(error)) {
    return { status: undefined, code: UNEXPECTED_CODE };
  }
  const err = error as { code?: unknown; response?: { status?: unknown } };
  const status = err.response?.status;
  const code = err.code;
  return {
    status: typeof status === 'number' ? status : undefined,
    code: typeof code === 'string' ? code : undefined,
  };
};
